import React from "react";
import { matchSorter } from 'match-sorter';

// Define a default UI for filtering
export const DefaultColumnFilter = ({ table: { getPrePaginationRowModel }, column: { getFilterValue, setFilterValue } }) => {
	let filterValue = getFilterValue();
	const count = getPrePaginationRowModel()?.rows?.length ?? 0;

	return (
		<input
			value={filterValue || ''}
			onChange={e => {
				setFilterValue(e.target.value || undefined) // Set undefined to remove the filter entirely
			}}
			placeholder={`Search ${count} records...`}
			style={{ width: '100%', maxWidth: '200px' }}
		/>
	);
};

// This is a custom filter UI for selecting a unique option from a list
export const SelectColumnFilter = ({ table: { getPrePaginationRowModel }, column: { getFilterValue, setFilterValue, id } }) => {
	const filterValue = getFilterValue();
	const preFilteredRows = getPrePaginationRowModel()?.rows;

	// Calculate the options for filtering
	// using the preFilteredRows
	const options = React.useMemo(() => {
		const options = new Set();
		preFilteredRows?.forEach(row => {
			options.add(row.original?.[id])
		});
		return [...options.values()];
	}, [id, preFilteredRows]);

	// Render a multi-select box
	return (
		<select
			value={filterValue}
			onChange={e => {
				setFilterValue(e.target.value || undefined)
			}}
		>
			<option value="">All</option>
			{options.map((option, i) => (
				<option key={i} value={option}>
					{option}
				</option>
			))}
		</select>
	);
};

// This is a custom filter UI that uses a slider to set the filter value between a column's min and max values
export const SliderColumnFilter = ({ table: { getPrePaginationRowModel }, column: { getFilterValue, setFilterValue, id } }) => {
	const filterValue = getFilterValue();
	const preFilteredRows = getPrePaginationRowModel()?.rows;

	// Calculate the min and max using the preFilteredRows
	const [min, max] = React.useMemo(() => {
		if (!preFilteredRows?.length) {
			return [0, 0];
		}

		// grab initial value safely, defaulting to 0 if nothing is found
		let initial = getNestedValue(preFilteredRows[0]?.original, id) ?? 0;
		let min = initial;
		let max = initial;

		preFilteredRows?.forEach(row => {
			const currentValue = getNestedValue(row?.original, id) ?? 0;
			min = Math.min(currentValue, min)
			max = Math.max(currentValue, max)
		});
		return [min, max];
	}, [id, preFilteredRows]);

	return (
		<>
			<input
				type="range"
				min={min}
				max={max}
				value={filterValue || min}
				onChange={e => {
					// console.log(e.target.value);
					setFilterValue(parseInt(e.target.value, 10))
				}}
			/>
			<button onClick={() => setFilterValue(undefined)}>Off</button>
		</>
	);
};


const getNestedValue = (obj, path) => {
	return path
		.split('.') // split on dot
		.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
};

// This is a custom UI for our 'between' or number range
// filter. It uses two number boxes and filters rows to
// ones that have values between the two
export const NumberRangeColumnFilter = ({ table: { getPrePaginationRowModel }, column: { getFilterValue, setFilterValue, id, ...otherProps } }) => {
	const filterValue = getFilterValue() ?? [];
	const preFilteredRows = getPrePaginationRowModel()?.rows;

	const [min, max] = React.useMemo(() => {
		if (!preFilteredRows?.length) {
			return [0, 0];
		}

		// grab initial value safely, defaulting to 0 if nothing is found
		let initial = getNestedValue(preFilteredRows[0]?.original, id) ?? 0;
		let min = initial;
		let max = initial;

		preFilteredRows?.forEach(row => {
			const currentValue = getNestedValue(row?.original, id) ?? 0;
			min = Math.min(currentValue, min)
			max = Math.max(currentValue, max)
		});
		return [min, max];
	}, [id, preFilteredRows]);

	return (
		<div
			style={{
				display: 'flex',
			}}
		>
			<input
				value={filterValue[0] || ''}
				type="number"
				onChange={e => {
					const val = e.target.value
					setFilterValue((old = []) => [val ? parseInt(val, 10) : undefined, old[1]])
				}}
				placeholder={`Min (${min})`}
				style={{
					width: '70px',
					marginRight: '0.5rem',
				}}
			/>
			to
			<input
				value={filterValue[1] || ''}
				type="number"
				onChange={e => {
					const val = e.target.value
					setFilterValue((old = []) => [old[0], val ? parseInt(val, 10) : undefined])
				}}
				placeholder={`Max (${max})`}
				style={{
					width: '70px',
					marginLeft: '0.5rem',
				}}
			/>
		</div>
	);
};

export const fuzzyTextFilterFn = (rows, id, filterValue) => {
	return matchSorter(rows, filterValue, { keys: [row => row.values[id]] });
};

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = val => !val;

// Define a custom filter filter function!
export const filterGreaterThan = (rows, id, filterValue) => {
	return rows.filter((row) => {
		const rowValue = row.values[id];
		return rowValue >= filterValue;
	});
};

// This is an autoRemove method on the filter function that
// when given the new filter value and returns true, the filter
// will be automatically removed. Normally this is just an undefined
// check, but here, we want to remove the filter if it's not a number
filterGreaterThan.autoRemove = val => typeof val !== 'number';